import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Shield, Wrench } from "lucide-react";

interface Plan {
  id: string;
  name: string;
  price: number;
  yearlyPrice: number;
  popular?: boolean;
  features: string[];
  cta: string;
  isCustom?: boolean;
}

const plans: Plan[] = [
  {
    id: "starter",
    name: "Starter",
    price: 199,
    yearlyPrice: 179,
    cta: "Select Plan",
    features: [
      "SMS summaries",
      "Job bookings + inquiries",
      "Call status checks",
      "Multilingual support (30+)",
      "Mid-conversation language switching",
      "Spam filtering",
      "10 simultaneous calls",
      "Basic analytics",
      "Cancel anytime"
    ]
  },
  {
    id: "growth",
    name: "Growth",
    price: 299,
    yearlyPrice: 269,
    popular: true,
    cta: "Select Plan",
    features: [
      "Everything in Starter plus:",
      "20 simultaneous calls",
      "Advanced scheduling",
      "Priority SMS",
      "Performance insights"
    ]
  },
  {
    id: "business-pro",
    name: "Business Pro",
    price: 499,
    yearlyPrice: 449,
    cta: "Select Plan",
    features: [
      "Everything in Growth plus:",
      "High-volume coverage",
      "Deeper history lookup",
      "Custom rules",
      "Priority human support",
      "Enhanced analytics"
    ]
  }
];

interface StepChoosePlanProps {
  onBack: () => void;
  onSelectPlan: (planId: string) => void;
}

export const StepChoosePlan = ({ onBack, onSelectPlan }: StepChoosePlanProps) => {
  const [isYearly, setIsYearly] = useState(true);

  return (
    <div className="space-y-12 pb-12">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Choose Your Plan</h1>
        <p className="text-muted-foreground">
          Select the perfect plan for your business needs
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-3 bg-muted rounded-full p-1">
          <button
            onClick={() => setIsYearly(false)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              !isYearly ? "bg-background shadow-sm" : ""
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setIsYearly(true)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              isYearly ? "bg-background shadow-sm" : ""
            }`}
          >
            Yearly
            <Badge className="ml-2 bg-primary">Save 10%</Badge>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-card rounded-2xl border p-6 space-y-6 transition-all hover:shadow-lg ${
              plan.popular ? "border-primary shadow-lg scale-105" : "border-border"
            }`}
          >
            {plan.popular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                Most Popular
              </Badge>
            )}

            <div className="space-y-2">
              <h3 className="text-xl font-bold">{plan.name}</h3>
              <div className="flex items-baseline gap-1">
                {plan.isCustom ? (
                  <span className="text-3xl font-bold">Custom</span>
                ) : (
                  <>
                    <span className="text-3xl font-bold">
                      ${isYearly ? plan.yearlyPrice : plan.price}
                    </span>
                    <span className="text-muted-foreground"> CAD/month</span>
                  </>
                )}
              </div>
            </div>

            <ul className="space-y-3">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className={feature.includes("Everything in") ? "font-semibold" : ""}>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => onSelectPlan(plan.id)}
              variant={plan.popular ? "default" : "outline"}
              className="w-full"
            >
              {plan.cta}
            </Button>
          </div>
        ))}
      </div>

      {/* All Plans Include */}
      <div className="bg-card rounded-2xl border border-border p-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          All Plans Include
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Check className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">24/7 Availability</h3>
            <p className="text-sm text-muted-foreground">
              Sophie AI never sleeps, never takes breaks
            </p>
          </div>
          <div className="space-y-2">
            <Check className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Instant Setup</h3>
            <p className="text-sm text-muted-foreground">
              Get up and running in minutes, not days
            </p>
          </div>
          <div className="space-y-2">
            <Check className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">No Long-Term Contracts</h3>
            <p className="text-sm text-muted-foreground">
              Cancel anytime with no penalties
            </p>
          </div>
        </div>
      </div>

      {/* 30-Day Guarantee */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl border border-primary/20 p-8">
        <div className="flex items-start gap-4">
          <div className="bg-primary/10 p-3 rounded-full">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">30-Day Zero-Risk Guarantee</h2>
            <p className="text-muted-foreground">
              Try Sophie AI risk-free for 30 days. If you're not completely satisfied, 
              we'll refund your first month—no questions asked.
            </p>
          </div>
        </div>
      </div>

      {/* Installation Fee */}
      <div className="bg-card rounded-2xl border border-border p-8">
        <div className="flex items-start gap-4">
          <div className="bg-muted p-3 rounded-full">
            <Wrench className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">One-Time Installation Fee — $450 CAD</h2>
            <p className="text-muted-foreground">
              This one-time setup fee covers custom configuration, knowledge base training, 
              and integration with your existing systems. It's a small investment for 
              a receptionist that works for you 24/7.
            </p>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={onBack} className="w-full max-w-xs">
          Back
        </Button>
      </div>
    </div>
  );
};
